import { connect } from "react-redux";
import { actions, ApplicationStore } from "src/store";
import { patchPaymentsRequest } from "src/store/actions/payments";
import { deleteVehicleRequest, postVehicleRatingRequest } from "src/store/actions/vehicles";
import {
    getDealerOfferDistanceByVin,
    getDealerOfferDownPaymentByVin,
    getDealerOfferFrequencyByVin,
    getDealerOfferPriceByVin,
    getDealerOfferTermByVin,
    getDealerOfferTotalAccessoriesByVin,
    getDealerOfferTotalIncentivesByVin,
    getDealerOfferTradeInAllowanceByVin,
    getDealerOfferTypeByVin,
} from "src/store/selectors/dealerOffer";
import { getVehicleMsrp, getVehiclesArrayUntilRefactor } from "src/store/selectors/vehicles";
import { getRegion } from "src/utils";
import { hasAccessoriesEnabled } from "src/utils/account";
import { ApiVehicle } from "Types/api/Vehicle";
import { VehiclePayments } from "Types/api/VehiclePayments";
import { VehicleState } from "Types/state";
import { determineVehiclePaddingSlot } from "./lib";
import TableCompare from "./TableCompare";
import { OfferFrequency } from "Types/api/OfferFrequency";

export function padVehicles(vehicles: VehicleState[]): VehicleState[] {
    const additionalVehicles = determineVehiclePaddingSlot(vehicles.length);
    if (additionalVehicles <= 0) {
        // No Padding Needed
        return [...vehicles];
    }

    // If we are here, we need to pad the vehicle array
    const fillerArray = new Array(additionalVehicles);
    fillerArray.fill(null);
    return [...vehicles, ...fillerArray];
}

export interface DealerVehicles {
    [vin: string]: DealerVehicle;
}

export interface DealerVehicle {
    msrp: number;
    price: number;
    incentivesTotal: number;
    purchasePrice: number;
    paymentType: string;
    frequency: OfferFrequency;
    term: number;
    leaseMileage: number;
    downPayment: number;
    tradeInEstimate: number;
    accessoriesTotal: number;
}

const mapStateToProps = (state: ApplicationStore) => {
    const { accessories, account, deal, onlineShopper, dealerOffer } = state;

    const dealerVehicles: DealerVehicles = Object.keys(dealerOffer).reduce((acc, vin) => {
        const price = getDealerOfferPriceByVin(state, vin);
        const incentivesTotal = getDealerOfferTotalIncentivesByVin(state, vin);

        acc[vin] = {
            msrp: getVehicleMsrp(state, vin),
            price,
            incentivesTotal,
            purchasePrice: price - incentivesTotal,
            paymentType: getDealerOfferTypeByVin(state, vin),
            frequency: getDealerOfferFrequencyByVin(state, vin),
            term: getDealerOfferTermByVin(state, vin),
            leaseMileage: getDealerOfferDistanceByVin(state, vin),
            downPayment: getDealerOfferDownPaymentByVin(state, vin),
            tradeInEstimate: getDealerOfferTradeInAllowanceByVin(state, vin),
            accessoriesTotal: getDealerOfferTotalAccessoriesByVin(state, vin),
        } as DealerVehicle;
        return acc;
    }, {});

    return {
        account,
        deal,
        dealerVehicles,
        hasAccountAccessories: hasAccessoriesEnabled(account),
        region: getRegion(account || {}),
        revision: deal.current_data_revision as number,
        srpLink: onlineShopper.srpLink,
        uuid: deal.uuid as string,
        vehicleAccessories: accessories,
        vehicles: getVehiclesArrayUntilRefactor(state),
        hasLeaseInGarage: [
            ...getVehiclesArrayUntilRefactor(state).filter((vehicle) => {
                return !(vehicle.vin in dealerVehicles);
            }),
            ...Object.values(dealerVehicles),
        ]
            .filter((vehicle) => !("loading" in vehicle) || !vehicle?.loading)
            .map((vehicle) => {
                if ("paymentType" in vehicle) {
                    return vehicle.paymentType;
                }
                return vehicle?.payments.selected;
            })
            .includes("lease"),
        dealerChanges: state.dealerChanges.changed,
        dealerAddedVehicles: state.dealerChanges.added,
    };
};

const mapDispatchToProps = (dispatch: any) => ({
    patchVehiclePayment: (requestBody: Partial<VehiclePayments>, uuid: string, vehicle: ApiVehicle) =>
        dispatch(patchPaymentsRequest(uuid, vehicle.vin, requestBody)),
    removeVehicle: (uuid: string, vin: string) => dispatch(deleteVehicleRequest(uuid, vin)),
    toggleOSModal: () => dispatch(actions.openModal.toggleModal(undefined, undefined, "click_add_vehicle")),
    updateVehicleRating: (uuid: string, vin: string, rating: number, prevRating?: number) => {
        dispatch(postVehicleRatingRequest(uuid, vin, rating, prevRating));
    },
});

export default connect(mapStateToProps, mapDispatchToProps)(TableCompare);
